<template>
    <div class="h-full flex flex-col bg-white">
        <div class="sticky top-0 z-50 bg-white w-full h-[88px] border-b border-gray-DFDFDF px-[20px] lg:px-[40px] flex items-center justify-center">
            <h2 class="font-semibold leading-none font-oceanside">
                {{ $t('ALL_COLOR_LOCATIONS_TITLE') }}
            </h2>
        </div>

        <div class="px-[20px] lg:px-[40px] py-[20px] flex-1 overflow-y-auto">
            <template v-if="colorGroups.length > 0">
                <div
                    v-for="group in colorGroups"
                    :key="group.key"
                    class="mb-8"
                >
                    <div class="font-semibold leading-none font-oceanside mb-[8px]">
                        {{ group.label }}
                    </div>
                    <div class="border-t border-gray-DFDFDF mb-[16px]"></div>

                    <div
                        v-for="(option, index) in group.options"
                        :key="`${group.key}-${option.name}`"
                        class="grid grid-cols-[1fr_auto] items-center bg-gray-F6F6F6 px-4 py-[8px] gap-2"
                        :class="{
                            'bg-gray-FAFAFA': index % 2 === 0,
                            'bg-white': index % 2 !== 0,
                        }"
                    >
                        <p class="font-oceanside leading-none">
                            {{ getOptionLabel(option, group) }}
                        </p>

                        <div class="flex items-center gap-4">
                            <div
                                class="w-[40px] h-[40px] rounded-full border border-gray-DFDFDF shrink-0"
                                :style="{
                                    backgroundColor: option.selectedColor || 'transparent',
                                    boxShadow: isColorTranslucent(option.selectedColor) ? 'inset 0 0 20px rgb(0, 0, 0)' : 'none',
                                }"
                            />

                            <button
                                type="button"
                                class="flex items-center gap-1 text-red underline font-oceanside leading-none"
                                @click="handleEdit(option)"
                            >
                                <span>{{ $t('EDIT') }}</span>
                                <img src="../assets/images/icons/edit.svg" class="w-[14px] h-[14px]" alt="" />
                            </button>
                        </div>
                    </div>
                </div>
            </template>

            <p
                v-else
                class="font-oceanside"
            >
                {{ $t('NO_COLOR_LOCATIONS_ASSIGNED') }}
            </p>
        </div>

        <div class="border-t border-gray-DFDFDF px-[20px] lg:px-[40px] py-[16px] bg-white h-[90px] flex items-center justify-center">
            <button
                type="button"
                :class="[
                    'h-[40px] min-h-[40px] rounded-full border border-red bg-red text-white leading-none font-oceanside w-full',
                    'font-semibold',
                    'focus:outline-none focus:ring-1 focus:ring-black focus:border-black',
                ]"
                @click="closePanel"
            >
                {{ $t('BACK_TO_DESIGN') }}
            </button>
        </div>
    </div>
</template>

<script>
    import allStores from '../stores/all';
    import getPlacementLabel from '../helpers/placementlabel';

    export default {
        name: 'AllColorLocations',

        mixins: [allStores],

        props: {
            payload: {
                type: Object,
                default: null,
            },
        },

        computed: {
            viewController() {
                return this.customizerStore.viewController;
            },

            colorGroups() {
                return Array.isArray(this.payload?.groups)
                    ? this.payload.groups
                    : [];
            },
        },

        methods: {
            isColorTranslucent(color) {
                if (!color) {
                    return false;
                }

                const selectedColor = color.toLowerCase();
                const paletteOption = this.customizerStore.masterPalette?.find((option) => (
                    option.color?.toLowerCase() === selectedColor
                    || option.value?.toLowerCase() === selectedColor
                ));

                return !!paletteOption?.translucent;
            },

            closePanel() {
                this.appStore.closeNestedPanel();
            },

            setSelectedGroup(itemData) {
                this.customizerStore.setCurrentGroup(itemData.name);
            },

            setShowGroupOverlay(value) {
                this.appStore.setShowGroupOverlay(value);
            },

            setShouldShowSummary(value) {
                this.appStore.setShouldShowSummary(value);
            },

            getRedirectTarget(option) {
                switch (option?.groupKey) {
                case 'top':
                    return { name: 'Top' };
                case 'backLogo':
                case 'backCavityDots':
                    return { name: 'Back' };
                case 'frontPaintfillWeights':
                case 'frontPaintfillLogos':
                case 'frontInitials':
                case 'frontStamps':
                    return { name: 'Face/Sole' };
                default:
                    return { name: 'Face/Sole' };
                }
            },

            async handleEdit(option) {
                const redirectTarget = this.getRedirectTarget(option);

                this.setShowGroupOverlay(false);
                this.closePanel();
                await new Promise((resolve) => { setTimeout(resolve, 250); });
                this.setSelectedGroup(redirectTarget);
                this.setShouldShowSummary(false);
                this.setShowGroupOverlay(true);
            },

            formatOptionName(optionName) {
                let formattedName = optionName;
                // remove 'Color' from the end of the option name
                formattedName = formattedName.replace('Color', '');
                // convert CamelCase to space separated words
                formattedName = formattedName.replace(/([A-Z])/g, ' $1').trim();
                return formattedName;
            },

            getPaintfillOptionLabel(option) {
                return getPlacementLabel(
                    this.viewController,
                    option.name,
                    this.$i18n,
                ) || this.formatOptionName(option.name);
            },

            getOptionLabel(option, group) {
                const groupKey = option?.groupKey || group?.key;

                if (
                    groupKey === 'top'
                    || groupKey === 'backLogo'
                    || groupKey === 'backCavityDots'
                    || groupKey === 'frontPaintfillWeights'
                    || groupKey === 'frontPaintfillLogos'
                ) {
                    return this.getPaintfillOptionLabel(option);
                }

                if (groupKey === 'frontInitials') {
                    return this.getInitialsOptionLabel(option) || this.formatOptionName(option.name);
                }

                if (groupKey === 'frontStamps') {
                    return this.getStampsOptionLabel(option) || this.formatOptionName(option.name);
                }

                return this.formatOptionName(option?.name || '');
            },

            getInitialsOptionLabel(option) {
                const initialsSelection = this.viewController?.selectedAtPlacement('StampingStyle');
                const colorName = this.getColorNameForOption(option);

                if (!initialsSelection || initialsSelection.placeholder) {
                    return null;
                }

                if (this.isLineOneColorOption(option)) {
                    const text = initialsSelection.custom?.['v-stamping-text-1'];
                    return this.joinLabelSections(
                        this.getInitialsSelectionLabel(initialsSelection),
                        this.joinSelectionParts(text || this.$t('CURRENT_COLOR'), colorName),
                    );
                }

                if (this.isLineTwoColorOption(option)) {
                    const text = initialsSelection.custom?.['v-stamping-text-2'];
                    return this.joinLabelSections(
                        this.getInitialsSelectionLabel(initialsSelection),
                        this.joinSelectionParts(text || this.$t('CURRENT_COLOR'), colorName),
                    );
                }

                if (this.isSymbolsColorOption(option)) {
                    const symbolName = this.getSelectionDescription('StampSymbols');
                    return this.joinLabelSections(
                        this.getInitialsSelectionLabel(initialsSelection),
                        this.joinSelectionParts(symbolName, colorName),
                    );
                }

                return null;
            },

            getStampsOptionLabel(option) {
                const colorName = this.getColorNameForOption(option);

                if (this.isStampColorOption(option)) {
                    const stampName = this.getSelectionDescription('Stamp');
                    return this.joinLabelSections(
                        this.getStampsSelectionLabel(),
                        this.joinSelectionParts(stampName, colorName),
                    );
                }

                return null;
            },

            joinSelectionParts(name, colorName) {
                const cleanName = `${name || ''}`.trim();
                const cleanColorName = `${colorName || ''}`.trim();

                if (cleanName && cleanColorName) {
                    return `${cleanName}: ${cleanColorName}`;
                }

                return cleanName || cleanColorName || null;
            },

            joinLabelSections(...sections) {
                return sections
                    .map((section) => `${section || ''}`.trim())
                    .filter((section) => section.length > 0)
                    .join(' | ');
            },

            getInitialsSelectionLabel(initialsSelection) {
                const custom = initialsSelection?.custom || {};
                const thumbnailAsset = custom['v-thumbnail-asset'] || '';
                const descriptionText = this.getDescriptionText(initialsSelection?.description);
                let letterStyle = '';

                if (thumbnailAsset.toLowerCase().includes('dancing')) {
                    letterStyle = 'Dancing';
                } else if (thumbnailAsset.toLowerCase().includes('straight')) {
                    letterStyle = 'Straight';
                }

                let label = 'Initials';

                if (letterStyle) {
                    label += `: ${letterStyle}`;
                }

                if (descriptionText.toLowerCase().includes('dots')) {
                    label += ' | Separate With Periods';
                }

                return label;
            },

            getStampsSelectionLabel() {
                const stampSelection = this.viewController?.selectedAtPlacement('Stamp');

                if (!stampSelection || stampSelection.placeholder || stampSelection.code === 'Stamp-Stamp-NoThanks') {
                    return null;
                }

                return 'Stamps: Face/Toe';
            },

            isLineOneColorOption(option) {
                return /line\s*1/i.test(this.formatOptionName(option?.name || ''));
            },

            isLineTwoColorOption(option) {
                return /line\s*2/i.test(this.formatOptionName(option?.name || ''));
            },

            isSymbolsColorOption(option) {
                return /symbol/i.test(option?.name || '');
            },

            isStampColorOption(option) {
                return option?.name === 'StampColor' || /stamp/i.test(option?.name || '');
            },

            getColorNameForOption(option) {
                return this.getSelectionDescription(option?.name)
                    || this.getPaletteColorLabel(option?.activeSlotValue)
                    || this.getPaletteColorLabel(option?.selectedColor)
                    || option?.selectedColor
                    || null;
            },

            getPaletteColorLabel(value) {
                if (!value) {
                    return null;
                }

                const match = this.customizerStore.masterPalette?.find((paletteOption) => (
                    paletteOption.value === value
                    || paletteOption.color === value
                ));

                return match?.label || null;
            },

            getSelectionDescription(placementCode) {
                const selection = this.viewController?.selectedAtPlacement(placementCode);

                if (!selection || selection.placeholder) {
                    return null;
                }

                return this.getDescriptionText(selection.description);
            },

            getDescriptionText(description) {
                if (!Array.isArray(description)) {
                    return '';
                }

                return description
                    .map((desc) => desc.name)
                    .filter(Boolean)
                    .join(' ')
                    .trim();
            },
        },
    };
</script>
